
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  Ref
} from "vue";
import { useAuth } from "../../../modules/auth";
import {
  FeaturePayload,
  FeatureClienteUpdatePayload,
  useFeatureService
} from "../../../modules/business/feature/featureBusiness";

interface FeatureSnapshot {
  idFeature: number;
  flHabilitada: boolean;
}

interface ClienteFeaturesState {
  features: Array<FeaturePayload>;
  original: Array<FeatureSnapshot>;
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  props: {
    idCliente: { type: Number, required: true },
    parentLoading: Boolean
  },
  setup(props) {
    const state = reactive<ClienteFeaturesState>({
      features: [],
      original: [],
      stateReady: false,
      loadings: []
    });

    const stateLoading = computed(() => {
      return state.loadings.length !== 0 || !!props.parentLoading;
    });

    const stateBlocked = computed(() => {
      return !!(
        stateLoading.value || !useAuth().amIAllowed("edit", "root-cliente")
      );
    });

    const setFeatures = (features: Array<FeaturePayload>) => {
      state.features = features.map((f) => ({
        ...f,
        flHabilitada: f.temRegistro ? f.flHabilitada : f.habilitada
      }));
      state.original = state.features.map((f) => ({
        idFeature: f.idFeature,
        flHabilitada: !!f.flHabilitada
      }));
    };

    onMounted(() => {
      const { data, loading, getFeaturesClienteRoot } = useFeatureService();
      state.loadings.push(loading);
      getFeaturesClienteRoot(props.idCliente)
        .then(() => {
          setFeatures(data.value ?? []);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.stateReady = true;
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    });

    // Chamado pelo CadastroCliente depois de salvar o cliente (botão único)
    const salvarFeatures = (): Promise<void> => {
      if (!state.stateReady) {
        return Promise.resolve();
      }

      const alteradas: Array<FeatureClienteUpdatePayload> = state.features
        .filter((f) => {
          const original = state.original.find(
            (o) => o.idFeature === f.idFeature
          );
          return !original || original.flHabilitada !== !!f.flHabilitada;
        })
        .map((f) => ({
          idFeature: f.idFeature,
          flHabilitada: !!f.flHabilitada
        }));

      if (!alteradas.length) {
        return Promise.resolve();
      }

      const { data, loading, updateFeaturesClienteRoot } = useFeatureService();
      state.loadings.push(loading);
      return updateFeaturesClienteRoot(props.idCliente, alteradas)
        .then(() => {
          setFeatures(data.value ?? []);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch((e) => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          throw e;
        });
    };

    return {
      stateLoading,
      stateBlocked,
      salvarFeatures,
      ...toRefs(state)
    };
  }
});
